import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserX, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { withReturnTo } from "@/lib/returnTo";
import FixyBeeMark from "@/components/brand/FixyBeeMark";

/**
 * "This link is for another account" (R11).
 *
 * The 17-sep run: the G1 cron mailed the closing reminder for job_53cf60722ddb
 * to its requester, who clicked it in a browser where a second account's
 * session was live. `ProtectedRoute` saw a role its route did not want and
 * redirected — silently — to that other account's dashboard, which was empty.
 * Nothing on screen said the link had been for somebody else, so the only
 * reading available was that the platform had lost the job.
 *
 * A redirect is the wrong answer to a deep link that names its recipient, and
 * so is a 403: neither of them is a mistake the visitor made. The right answer
 * is to say who the link was for and offer the one action that fixes it.
 *
 * **Logging out navigates to `/login` carrying this exact path as `returnTo`**,
 * so signing in with the right account lands on the page the email pointed at
 * rather than on a dashboard — the same drop `lib/returnTo` was written to stop.
 */
export default function WrongAccountNotice({ hint, target }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const role = user?.profile?.role;
  const ownDashboard = role === "provider" ? "/provider/dashboard" : "/client/dashboard";

  const switchAccount = async () => {
    setBusy(true);
    // The navigation happens whether or not the logout call succeeded: the
    // cookie is gone either way from this browser's point of view, and landing
    // on a login screen is recoverable while staying here is not.
    try { await logout(); } finally {
      navigate(withReturnTo("/login", target), { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <div className="card-base p-8 max-w-md w-full text-center" data-testid="wrong-account-notice">
        <div className="flex items-center justify-center gap-3">
          <FixyBeeMark size={32} />
          <UserX className="text-smoke" size={28} />
        </div>
        <h1 className="mt-4 font-display text-2xl font-bold text-worker">
          {t("wrongAccount.title")}
        </h1>
        {/* The masked address the link carried — the recipient's, not the
            session's. Without it this screen could only say "another account",
            which does not tell anyone which inbox to look in. */}
        <p className="mt-2 text-sm text-smoke" data-testid="wrong-account-hint">
          {hint
            ? t("wrongAccount.bodyWithHint", { email: hint })
            : t("wrongAccount.body")}
        </p>
        {user?.email && (
          <p className="mt-2 text-xs text-smoke" data-testid="wrong-account-current">
            {t("wrongAccount.signedInAs", { email: user.email })}
          </p>
        )}
        <button
          onClick={switchAccount}
          disabled={busy}
          className="fx-btn fx-btn--primary mt-6 mx-auto"
          data-testid="wrong-account-switch"
        >
          <LogOut size={16} /> {busy ? t("common.loading") : t("wrongAccount.switch")}
        </button>
        <button
          onClick={() => navigate(ownDashboard, { replace: true })}
          className="fx-btn fx-btn--ghost mt-3 mx-auto"
          data-testid="wrong-account-stay"
        >
          <LayoutDashboard size={16} /> {t("wrongAccount.stay")}
        </button>
      </div>
    </div>
  );
}
